import {AxiosError, AxiosResponse} from 'axios';
import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from 'react-query';
import {QueryFunctionContext} from 'react-query/types/core/types';
import {api} from './api';

type QueryKeyT = [string, object | undefined];

interface Pagination {
  currentPage: number;
  hasMore: boolean;
  totalCnt?: number;
}

export interface GetInfinitePagesInterface<T> {
  nextId?: number;
  previousId?: number;
  data: T[];
  pagination: Pagination;
  count: number;
}

export const fetcher = <T>({
  queryKey,
  pageParam,
}: QueryFunctionContext<QueryKeyT>): Promise<T> => {
  const [url, params] = queryKey;
  // console.log('pageParam', pageParam); // 디버그용 로그
  return api()
    .get<T>(url, {params: {...params, ...pageParam}})
    .then(res => res.data);
};

export const useLoaderMoreRefecting = <T>(
  url: string | null,
  params?: object,
  refetchingTime?: number,
) => {
  const context = useInfiniteQuery<
    GetInfinitePagesInterface<T>,
    Error,
    GetInfinitePagesInterface<T>,
    QueryKeyT
  >(
    [url!, params],
    ({
      queryKey,
      pageParam = {
        page: 1,
      },
    }) => {
      return fetcher({queryKey, pageParam, meta: {}});
    },
    {
      getPreviousPageParam: firstPage => firstPage.previousId ?? false,
      getNextPageParam: lastPage => {
        if (lastPage.pagination?.hasMore) {
          return {page: lastPage.pagination.currentPage + 1};
        }
        return undefined;
      },
      refetchOnWindowFocus: false,
      refetchInterval: refetchingTime,
    },
  );

  return context;
};

export const useLoadMoreWithSuspense = <T>(
  url: string | null,
  params?: object,
) => {
  const context = useInfiniteQuery<
    GetInfinitePagesInterface<T>,
    Error,
    GetInfinitePagesInterface<T>,
    QueryKeyT
  >(
    [url!, params],
    ({
      queryKey,
      pageParam = {
        page: 1,
      },
    }) => {
      return fetcher({queryKey, pageParam, meta: {}});
    },
    {
      getPreviousPageParam: firstPage => firstPage.previousId ?? false,
      getNextPageParam: lastPage => {
        if (lastPage.pagination?.hasMore) {
          return {page: lastPage.pagination.currentPage + 1};
        }
        return undefined;
      },
      refetchOnWindowFocus: false,
      suspense: true,
    },
  );

  return context;
};

export const useLoadMore = <T>(
  url: string | null,
  params?: object,
  options?: UseInfiniteQueryOptions<
    GetInfinitePagesInterface<T>,
    Error,
    GetInfinitePagesInterface<T>,
    GetInfinitePagesInterface<T>,
    QueryKeyT
  >,
) => {
  const context = useInfiniteQuery<
    GetInfinitePagesInterface<T>,
    Error,
    GetInfinitePagesInterface<T>,
    QueryKeyT
  >(
    [url!, params],
    ({
      queryKey,
      pageParam = {
        page: 1,
      },
    }) => {
      return fetcher({queryKey, pageParam, meta: {}});
    },
    {
      getPreviousPageParam: firstPage => firstPage.previousId ?? false,
      getNextPageParam: lastPage => {
        if (lastPage.pagination?.hasMore) {
          return {page: lastPage.pagination.currentPage + 1};
        }
        return undefined;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
  );

  return context;
};

export const usePrefetch = <T>(url: string | null, params?: object) => {
  const queryClient = useQueryClient();

  return () => {
    if (!url) {
      return;
    }

    queryClient.prefetchQuery<T, Error, T, QueryKeyT>(
      [url!, params],
      ({queryKey}) => fetcher({queryKey, meta: {}}),
    );
  };
};

export const useFetch = <T>(
  url: string | null,
  params?: object,
  config?: UseQueryOptions<T, Error, T, QueryKeyT>,
) => {
  const context = useQuery<T, Error, T, QueryKeyT>(
    [url!, params],
    ({queryKey}) => {
      return fetcher({queryKey, meta: {}});
    },
    {
      enabled: !!url,
      ...config,
    },
  );

  return context;
};

export const useSuspenseFetch = <T>(
  url: string | null,
  params?: object,
  config?: UseQueryOptions<T, Error, T, QueryKeyT>,
) => {
  const context = useQuery<T, Error, T, QueryKeyT>(
    [url!, params],
    ({queryKey}) => fetcher({queryKey, meta: {}}),
    {
      enabled: !!url,
      suspense: true,
      ...config,
    },
  );

  return context;
};

export const useFetchAll = <T>(
  requests: {url: string; params?: object}[],
  config?: UseQueryOptions<T[], Error, T[], QueryKeyT[]>,
) => {
  const fetchAll = async ({
    queryKey,
  }: QueryFunctionContext<QueryKeyT[]>): Promise<T[]> => {
    const fetchPromises = queryKey.map(([url, params]) =>
      fetcher<T>({queryKey: [url, params], meta: {}}),
    );
    return Promise.all(fetchPromises);
  };

  const queryKey = requests.map(({url, params}) => [url, params] as QueryKeyT);

  const context = useQuery<T[], Error, T[], QueryKeyT[]>(queryKey, fetchAll, {
    enabled: requests.length > 0,
    ...config,
  });

  return context;
};

export const useFetchKey = <T>(
  url: string | null,
  keys: object,
  params?: object,
  config?: UseQueryOptions<T, Error, T, QueryKeyT>,
) => {
  const context = useQuery<T, Error, T, QueryKeyT>(
    [url!, keys],
    () =>
      fetcher({
        queryKey: [url!, params],
        meta: {},
      }),
    {
      enabled: !!url,
      ...config,
    },
  );

  return context;
};

const useGenericMutation = <T, S>(
  func: (data: T | S) => Promise<AxiosResponse<S>>,
  url: string,
  params?: object,
  updater?: ((oldData: T, newData: S) => T) | undefined,
) => {
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse, AxiosError, T | S>(
    data => {
      return func(data);
    },
    {
      onMutate: async data => {
        await queryClient.cancelQueries([url!, params]);

        const previousData = queryClient.getQueryData([url!, params]);

        queryClient.setQueryData<T>([url!, params], oldData => {
          return updater ? updater(oldData!, data as S) : (data as T);
        });

        return previousData;
      },
      onError: (err, _, context) => {
        queryClient.setQueryData([url!, params], context);
      },
      onSettled: () => {
        queryClient.invalidateQueries([url!, params]);
      },
    },
  );
};

export const useDelete = <T>(
  url: string,
  params?: object,
  updater?: (oldData: T, id: string | number) => T,
  headers?: object,
) => {
  return useGenericMutation<T, string | number>(
    data => api(headers).delete(url, data),
    url,
    params,
    updater,
  );
};

export const usePost = <T, S>(
  url: string,
  params?: object,
  updater?: (oldData: T, newData: S) => T,
  headers?: object,
) => {
  return useGenericMutation<T, S>(
    data => api(headers).post<S>(url, data),
    url,
    params,
    updater,
  );
};

export const useUpdate = <T, S>(
  url: string,
  params?: object,
  updater?: (oldData: T, newData: S) => T,
  headers?: object,
) => {
  return useGenericMutation<T, S>(
    data => api(headers).patch<S>(url, data),
    url,
    params,
    updater,
  );
};

export const usePut = <T, S>(
  url: string,
  params?: object,
  updater?: (oldData: T, newData: S) => T,
  headers?: object,
) => {
  return useGenericMutation<T, S>(
    data => api(headers).put<S>(url, data),
    url,
    params,
    updater,
  );
};
